import React, { useState, useRef, useEffect, useMemo, useCallback } from 'react';
import { FaArrowLeft, FaSave, FaExchangeAlt, FaPlus, FaFileExcel, FaFileCsv, FaHistory, FaChevronDown, FaChevronRight, FaExclamationTriangle, FaTimes, FaCheck, FaPaperPlane, FaPlaneDeparture, FaInfoCircle } from 'react-icons/fa';
import { toast } from 'react-toastify';
import axios from 'axios';
import { useHistory } from 'react-router-dom';
import api from '../../services/api';
import notify from '../../services/notify';
import ErpTemplate from '../../components/ErpTemplate';
import UploadDropzone from '../../components/UploadDropzone';
import AjustarOpcoesModal from '../../components/AjustarOpcoesModal';
import CabecalhoModeloCliente from '../../components/CabecalhoModeloCliente';
import CabecalhoEventoAereo from '../../components/CabecalhoEventoAereo';
import { useAtendimentoAereo } from '../../components/AtendimentoAereo';
import PainelTrechos from '../../components/PainelTrechos';
import GrupoMalhaModal from '../../components/GrupoMalhaModal';
import NovaOpcaoMalhaModal from '../../components/NovaOpcaoMalhaModal';
import PlanilhaMalhaAerea, { MERGE_KEYS, COLS_CALCULADAS } from '../../components/PlanilhaMalhaAerea';
import ConfirmarAcaoModal, { ObservacaoObrigatoria, avisoOpcoesNoEvento } from '../../components/ConfirmarAcaoModal';
import { numerarPorGrupo, validar, valorComum, calcularTotal, normalizarMerge, extrasDaMescla, valorDoModal, linhasNoRunDoModal, vincularOrfas, vincularPorCidade, opcaoVazia, vinculoDe, baseDoVinculo, comVinculo, CAMPOS_GRUPO, ROTULO_SITUACAO, COR_SITUACAO, cuidaDoPedido, acoesDeImportacao, selecaoDeImportacao, contarLadosMarcados, lerSelecaoDaMalha, guardarSelecaoDaMalha, PERFIS_AEREO } from '../../util/malhaAereo';
// Esta tela usa `fin-pp__pill` e `fin-pp__modal` direto — o CSS vinha por carona do
// GrupoMalhaModal, o que quebraria no dia em que aquele import saísse daqui.
import { nomeDoArquivo } from '../../util/util';
import { lerRascunhoMalha, salvarRascunhoMalha } from '../../util/rascunhoMalha';
import '../../css/financeiro-parcela-pagar.css';

// Tipo detectado no upload (mesmos tipos do backend), na mini-tag da tabela do histórico.
const TIPO_BADGE = {
    nacional_ida:     { label: 'Nacional · Ida',     tom: 'is-info' },
    nacional_retorno: { label: 'Nacional · Retorno', tom: '' },
    internacional:    { label: 'Internacional',      tom: 'is-warn' },
};

// Avisos da aprovação de Eventos. As CHAVES vêm do back (EventoAereoAprovacaoService::motivos);
// o texto é da tela, mesma divisão do resto do domínio. O que exige a aprovação é só o evento
// internacional. Motivo desconhecido é ignorado.
const TEXTO_MOTIVO = {
    internacional: 'o evento é internacional',
};
const motivosTexto = (motivos) => {
    const textos = (motivos ?? []).map(m => TEXTO_MOTIVO[m]).filter(Boolean);
    return textos.length ? 'Atenção: ' + textos.join(' e ') + '.' : null;
};

// "2026-06-09 14:30:00" → "09/06/2026 14:30"
const formatarData = (d) => {
    if (!d) return '';
    const [data, hora] = String(d).split(' ');
    const [a, m, dia] = (data || '').split('-');
    return (dia && m && a) ? `${dia}/${m}/${a}${hora ? ' ' + hora.slice(0, 5) : ''}` : String(d);
};

const CAMPOS_CABECALHO = [
    'cliente', 'nomeEvento', 'produto', 'bu', 'centroCusto', 'ordemInterna',
    'solicitante', 'agenciaIntermediaria', 'consultor', 'emailConsultor',
    'telefoneConsultor', 'poAereo', 'valorPedidoBalao', 'periodoEvento', 'observacoesGerais',
];
const pickCabecalho = (m) => CAMPOS_CABECALHO.reduce((acc, k) => ({ ...acc, [k]: m?.[k] ?? '' }), {});

// Subconjunto de merge realmente editável cujo vazio é auto-preenchido ao Salvar
// (tktPessoa/tktGrupo são calculados).
const MERGE_FILL_KEYS = MERGE_KEYS.filter(k => !COLS_CALCULADAS.includes(k));

// id estável por opção (handle do board/juntar; o backend ignora _id).
const comIds = (ops) => (ops ?? []).map(op => op._id ? op : { ...op, _id: 'op_' + Math.random().toString(36).slice(2, 10) });

// Trecho ou origem do pedido sem edição do Aéreo nesta malha: a primeira edição os transforma em
// "editada", com o valor de antes guardado para o Reverter.
const intacto = (s) => s?.tipo === 'trecho' || s?.tipo === 'origem';
const prepara = (ops) => comIds(numerarPorGrupo(ops));

/**
 * A malha de uma solicitação, pelo id dela. `sugestao` ({ idEvento }): a mesma tela para a sugestão de voos do evento
 * (`CadastroEventoSugestaoAereo`), que não tem solicitação. Nela, o cabeçalho do Modelo Cliente sai, o painel de
 * agrupamento se chama "Grupos", o "Vincular órfãs" cria o grupo da cidade que falta (`vincularPorCidade`), e o
 * rodapé do Aéreo é só o Salvar, que vira "Voltar para a emissão" com a tela igual ao que está gravado. Ela vai ao
 * RSVP no Enviar da emissão.
 *
 * `rascunho`: a mesma tela no Rascunho de Malha (`RascunhoMalhaAereo`), uma ferramenta do Aéreo sem evento nem
 * solicitação. Como a sugestão, ela agrupa pela cidade; o upload só lê a planilha, o Salvar guarda a tela neste
 * navegador (`util/rascunhoMalha`), e não há cabeçalho do Modelo Cliente, trava nem aprovação. O Exportar sai só da
 * tela, sem o cabeçalho do evento nem a aba Orientações.
 */
export default function CadastroEventoImportarMalhaAereo({ match, sugestao = null, rascunho = false }) {
    const history  = useHistory();
    const idMalha = match.params.idMalha;
    const perfil   = localStorage.getItem('perfil');
    const token    = localStorage.getItem('token');
    const nomeUsuario = localStorage.getItem('nomeUsuario');
    // O admin acumula os DOIS papéis, e a tela abria com os dois ligados: o upload e a edição do
    // Aéreo ao lado da coluna de importar do RSVP — uma tela que nenhum dos dois tem, e em que
    // nenhum dos dois fluxos se lê inteiro. O seletor troca o que ele VÊ. Não é permissão: o back
    // continua deixando o admin fazer os dois, e quem não é admin não tem seletor nenhum.
    const ehAdmin = perfil === 'admin';
    const [visaoAdmin, setVisaoAdmin] = useState('aereo');
    // O admin é do grupo 'admin' no back, e o back o deixa fazer tudo. Aqui a visão vira um papel
    // do fluxo, para a tela ser a de UM deles. 'eventos' cai em `papel = 'evento'` porque é assim
    // que o perfil se chama, e é o que o `cuidaDoPedido` compara.
    const PAPEL_DA_VISAO = { aereo: 'aereo', eventos: 'evento', rsvp: 'rsvp' };
    const papel = ehAdmin ? PAPEL_DA_VISAO[visaoAdmin] : perfil;
    // `isAereo` (o papel do Aéreo com a malha liberada) fica depois do carregamento: depende do
    // atendimento do evento, que só se sabe com o cabeçalho.
    // Ação do RSVP. O back deixa o admin fazer de qualquer visão — a permissão é a mesma —, mas a
    // tela é de UM papel por vez: olhando como Eventos, pedir alteração, confirmar a alterada e
    // importar não são dele agora. Quem não é admin não tem visão para escolher, e vale o que o
    // back disse e nada mais.
    const acaoDoRsvp = (pode) => !!pode && (!ehAdmin || visaoAdmin === 'rsvp');
    // A Importação das Opções de Aéreo é do RSVP: o atalho do cabeçalho só aparece para ele.
    const abreImportacao = cuidaDoPedido(papel);
    // Sem solicitação (a sugestão e o rascunho), o painel é de grupos, e a linha solta vai para o grupo da cidade dela.
    const semPedido = !!sugestao || rascunho;

    const [headerEvento,   setHeaderEvento]   = useState({}); // dados enxutos p/ o cabeçalho
    const [arquivo,        setArquivo]        = useState(null);
    const [opcoes,         setOpcoes]         = useState([]);
    const [carregando,     setCarregando]     = useState(false); // parse do arquivo
    const [buscando,       setBuscando]       = useState(false); // GET inicial da malha salva
    const [precisaTipo,    setPrecisaTipo]    = useState(false);
    const [arquivoPendente, setArquivoPendente] = useState(null);
    const [sugestaoTipo,   setSugestaoTipo]   = useState(null); // sugestão do backend p/ o modal de tipo
    const [sugestaoMotivo, setSugestaoMotivo] = useState('');   // evidência curta da sugestão
    const [cabecalho,      setCabecalho]      = useState({});    // cabeçalho do Modelo Cliente
    const [padroes,        setPadroes]        = useState({});    // valores do cadastro do evento (verdes)
    const [produtosDoEvento, setProdutosDoEvento] = useState([]); // opções do Produto / BU: os produtos do orçamento
    const [arquivosHist,   setArquivosHist]   = useState([]);    // histórico de uploads (eventomalhaarquivo)
    const [histAberto,     setHistAberto]     = useState(false); // accordion do histórico de uploads
    const [trechos,   setTrechos]   = useState([]);    // trechos do evento (validação)
    const [pedido,    setPedido]    = useState(null);  // destino, datas e observação da solicitação (só leitura)
    const [painelAberto,   setPainelAberto]   = useState(false); // accordion Trechos (liga as cores)
    const [focoMalha,      setFocoMalha]      = useState(null);  // { ids, vez }: o que o painel pediu para focar
    const [itemEditando,   setItemEditando]   = useState(null);  // vínculo aberto no modal do lápis
    const opcoesRef = useRef(opcoes);
    opcoesRef.current = opcoes;

    // Modal de pareamento IDA & RETORNO
    const [ajustarAberto, setAjustarAberto] = useState(false);
    const [novaLinhaAberta, setNovaLinhaAberta] = useState(false);
    const [salvando, setSalvando] = useState(false);
    const [exportando, setExportando] = useState(false);
    // Estado da aprovação, calculado no back: grupo de quem olha, situação, e para cada ação se
    // pode e por que não. A tela só lê.
    const [aprovacao, setAprovacao] = useState(null);
    const [decidindo, setDecidindo] = useState(false);
    // Ação à espera de confirmação: 'enviar' | 'aprovar' | 'reprovar' | 'alteracao' |
    // 'revisarAlteracao' | 'importar'. Toda ação que passa a vez para outro cargo confirma antes —
    // um clique errado avisa outra pessoa, muda a etapa e pode apagar aprovação.
    const [confirmacao, setConfirmacao] = useState(null);
    // Observação da reprovação e do pedido de alteração: obrigatória, vai no e-mail ao Aéreo e
    // não é gravada.
    const [observacao, setObservacao] = useState('');
    // Leitura da malha recusada (rascunho do Aéreo aberto por outro cargo) ou com falha: a tela diz
    // por quê.
    const [bloqueioLeitura, setBloqueioLeitura] = useState(null);
    // A malha salva já foi lida. O upload só libera depois: sem a leitura a tela fica com a malha
    // vazia, e o upload, que salva a tela antes de importar, gravaria esse vazio por cima dela.
    const [malhaLida, setMalhaLida] = useState(false);
    // Na sugestão de voos, o payload do último carregamento, Salvar ou upload: com a tela igual a ele, o Salvar vira
    // "Voltar para a emissão". `marcarGravado` pede a foto no render em que o estado gravado chega à tela.
    const [gravado, setGravado] = useState(null);
    const marcarGravadoRef = useRef(false);
    const marcarGravado = () => { marcarGravadoRef.current = true; };

    // Importação das opções desta malha, quando aprovada: o estado de cada opção no evento vem
    // da prévia da Importação das Opções de Aéreo (a mesma da tela do evento), e a escolha é por
    // lado — só a ida, só a volta, ou os dois.
    const [previaMalha, setPreviaMalha] = useState(null);
    // { [id da opção da malha]: { ida, retorno } }, guardado por malha (`lerSelecaoDaMalha`).
    const [marcados, setMarcados] = useState(() => lerSelecaoDaMalha(idMalha));
    const [importando, setImportando] = useState(false);

    // Ids das opções já importadas para o evento (vêm do back em `aprovacao`): marcam a linha
    // "No evento" e travam o X do chip, espelhando a trava do Salvar.
    const portadasIds = useMemo(
        () => new Set((aprovacao?.opcoesPortadas ?? []).map(String)),
        [aprovacao]
    );

    // Carrega o estado salvo da malha ao abrir a tela. O do rascunho está neste navegador.
    useEffect(() => {
        if (rascunho) {
            lerRascunhoMalha().then(salvo => {
                if (salvo) {
                    setOpcoes(prepara(salvo.opcoes ?? []));
                    setTrechos(salvo.trechos ?? []);
                }
                setMalhaLida(true);
            });
            return;
        }
        if (!idMalha) return;
        setBuscando(true);
        api({
            method: 'get',
            action: '/eventoAereoMalhaOpcao',
            token,
            params: { malha: idMalha },
        }).then(res => {
            const malha = res.data.data?.malha;
            if (malha) setCabecalho(pickCabecalho(malha));
            setPadroes(res.data.data?.padroes ?? {});
            setProdutosDoEvento(res.data.data?.produtosDoEvento ?? []);
            const salvas = res.data.data?.opcoes;
            if (Array.isArray(salvas) && salvas.length) setOpcoes(prepara(salvas));
            setTrechos(res.data.data?.trechos ?? []);
            setPedido(res.data.data?.pedido ?? null);
            setAprovacao(res.data.data?.aprovacao ?? null);
            const hist = res.data.data?.arquivos ?? [];
            setArquivosHist(Array.isArray(hist) ? hist : []);
            setMalhaLida(true);
            marcarGravado();
        }).catch(err => {
            // Vira mensagem na tela em vez de uma malha vazia, que pareceria sem cotação. 403 é a
            // trava do rascunho e 404 é malha que não existe mais — nos dois o back escreveu a
            // frase exata, e ela vale mais que qualquer genérica nossa. Só a falha sem mensagem
            // cai no aviso de baixo. Em todos os casos o upload fica travado.
            const status = err?.response?.status;
            const doServidor = err?.response?.data?.error || err?.response?.data?.mensagem;
            if (status === 403 || status === 404) {
                setBloqueioLeitura(doServidor || 'Esta malha ainda está com o Aéreo.');
                return;
            }
            setBloqueioLeitura('Não foi possível carregar a malha. O upload fica travado até ela carregar: atualize a página.');
            notify.apiError(err, 'Erro ao carregar a malha');
        })
          .finally(() => setBuscando(false));
        api({ method: 'get', action: '/eventoAereoCabecalho', token, params: { malha: idMalha } })
            .then(res => setHeaderEvento(res.data?.data ?? {}))
            .catch(() => {});
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Assumido o atendimento, relê só as travas do back (`aprovacao`): sem atender, o Aéreo não
    // editava nada, então as opções da tela continuam as que vieram do banco.
    const relerTravas = useCallback(() => {
        if (!idMalha) return;
        api({ method: 'get', action: '/eventoAereoMalhaOpcao', token, params: { malha: idMalha } })
            .then(res => setAprovacao(res.data.data?.aprovacao ?? null))
            .catch(() => {});
    }, [idMalha, token]);
    // Atendimento do Aéreo: o Aéreo só opera a malha do evento que atende; o gerente (e o admin na
    // visão do Aéreo) opera sempre. Sem operar, a tela fica como a de quem só lê.
    const atendimento = useAtendimentoAereo(headerEvento.evento, relerTravas);
    // O rascunho não tem evento para atender nem trava: quem abre a tela (o Aéreo, pela rota) monta a malha.
    const isAereo  = rascunho || (PERFIS_AEREO.includes(papel) && atendimento.opera);

    // Estado da tela no formato do PUT da malha. O Salvar grava isto e o Exportar monta a
    // planilha com isto, sem gravar: a planilha tem de ser exatamente o que o Salvar gravaria.
    const payloadMalha = () => ({
        // Descarta a linha que ficou sem nenhum dado (esvaziada à mão na tabela): ela segue
        // na tela, mas não vai ao banco nem ao .xlsx. RENUMERA depois de filtrar:
        // a numeração da tela conta a linha vazia, então sem isto o grupo iria com buraco
        // (1, 3) para o banco e para o Modelo Cliente.
        opcoes: normalizarMerge(numerarPorGrupo(opcoes.filter(op => !opcaoVazia(op))), MERGE_FILL_KEYS,
            (sid, campo) => extrasDaMescla(sid, campo, trechos)),
        cabecalho,
        ordemTrechos: trechos.map(s => s.id),
        // Edições e grupos numa lista só. A `ordem` é o índice na MESMA lista dos trechos —
        // é o que faz as duas tabelas dividirem a numeração. Grupo criado na tela vai com id
        // temporário; o back troca pelo real.
        grupos: trechos
            .map((s, i) => ({ s, ordem: i }))
            .filter(({ s }) => s.tipo === 'grupo' || s.tipo === 'editada')
            .map(({ s, ordem }) => ({
                id: s.grupo,
                // A edição aponta para o que ela sobrescreve: o trecho ou a origem do pedido
                // (`editado_tipo` + `editado_id` no back). O grupo avulso não sobrescreve nada.
                editadoTipo: s.tipo === 'editada' ? baseDoVinculo(s) : null,
                editadoId: s.tipo === 'editada' ? s.id : null,
                ordem,
                // Sobe a SOBRESCRITA, não os valores efetivos: vazio aqui grava NULL, que é o
                // "não sobrescreve" do banco. Mandar o efetivo congelaria como próprio tudo o que o
                // trecho só herdava. Sem sobrescrita registrada (grupo avulso), o valor é o dele.
                ...CAMPOS_GRUPO.reduce((acc, k) => ({ ...acc, [k]: (s.sobrescrita ?? s)[k] ?? null }), {}),
            })),
    });

    useEffect(() => {
        if (!sugestao || !marcarGravadoRef.current) return;
        marcarGravadoRef.current = false;
        setGravado(JSON.stringify(payloadMalha()));
    });
    const semMudanca = !!sugestao && gravado !== null && JSON.stringify(payloadMalha()) === gravado;

    // PUT da malha (opções + cabeçalho + ordem dos trechos). Retorna a promise p/ encadear:
    // `salvar` mostra o toast; o Enviar para aprovação aguarda antes de enviar.
    // DEDUPLICA chamadas concorrentes reaproveitando a requisição em andamento — duas
    // gravações em paralelo faziam delete+recreate sobreposto e DUPLICAVAM as opções.
    const persistindoRef = useRef(null);
    // `silencioso`: sem o aviso de "salva" — o upload salva antes de importar, e o aviso que
    // importa ali é o da importação.
    const persistir = ({ silencioso = false } = {}) => {
        // No rascunho, o Salvar guarda a tela neste navegador, e nada vai ao sistema.
        if (rascunho) {
            return salvarRascunhoMalha({ opcoes, trechos }).then(ok => {
                if (!ok) toast.error('Este navegador não deixou salvar o rascunho.');
                else if (!silencioso) toast.success('Rascunho salvo neste navegador');
            });
        }
        if (persistindoRef.current) return persistindoRef.current;
        const p = api({
            method: 'put',
            action: "/eventoAereoMalhaOpcao/" + idMalha,
            token,
            data: payloadMalha(),
        })
            // Recarrega os vínculos: grupo criado na tela subiu com id temporário e
            // só aqui ganha o id real — tanto na linha quanto no `grupo` das opções.
            // Sem isto o próximo Salvar mandaria um vínculo apontando para id morto.
            .then(() => {
                // Avisa AQUI, não no fim da cadeia: o PUT terminou e a malha está gravada.
                // Esperar o recarregamento atrasava o aviso num GET lento e, se o GET
                // falhasse, virava "Erro ao salvar" numa malha que salvou.
                //
                // Avisa TAMBÉM no Enviar para aprovação, que salva antes de enviar: é o único
                // retorno que o operador tem de que aquele passo aconteceu.
                if (!silencioso) toast.success('Malha salva com sucesso');
                setBuscando(true);
                // O catch fica NESTE ramo, não na cadeia toda: falha do recarregamento não
                // desfaz o que foi gravado e não pode rejeitar o `persistir` — senão o
                // `salvar` acusaria erro e o Enviar deixaria de enviar uma malha gravada. Uma
                // rejeição do PUT continua subindo, que é o que o chamador precisa saber.
                return api({ method: 'get', action: '/eventoAereoMalhaOpcao', token, params: { malha: idMalha } })
                    .then(res => {
                        setTrechos(res.data.data?.trechos ?? []);
                        // O Salvar pode ter acendido o aviso de alteração: o estado volta junto.
                        setAprovacao(res.data.data?.aprovacao ?? null);
                        const salvas = res.data.data?.opcoes;
                        if (Array.isArray(salvas)) setOpcoes(prepara(salvas));
                        marcarGravado();
                        return res;
                    })
                    .catch(err => notify.apiError(err, 'Malha salva, mas não foi possível recarregar. Atualize a página.'));
            })
            .finally(() => {
                setBuscando(false);
                persistindoRef.current = null;
            });
        persistindoRef.current = p;
        return p;
    };

    // Decisão sobre a malha. Não passa pelo `persistir`: quem decide não edita a malha, e
    // mandar as opções junto numa aprovação gravaria a tela de quem só estava olhando.
    const decidir = (acao) => {
        setDecidindo(true);
        // Envio da malha: a vez passa para Eventos (ou para o RSVP, quando responde a um pedido
        // de alteração), e o Aéreo volta ao painel de Malhas, onde vê a situação da rodada. Só
        // depois de o estado da tela assentar.
        let voltarAoPainel = false;
        const enviandoAlteracao = acao === 'enviar' && !!aprovacao?.enviar?.alterada;
        api({
            method: 'post',
            action: '/eventoMalhaAereaAprovacao',
            token,
            data: {
                malha: idMalha, acao,
                ...(['reprovar', 'alteracao', 'notificar'].includes(acao) ? { observacao } : {}),
            },
        })
            .then(res => {
                const rotulo = {
                    enviar: enviandoAlteracao ? 'Alteração enviada ao RSVP' : 'Malha enviada para a aprovação de Eventos',
                    aprovar: 'Malha aprovada',
                    reprovar: 'Malha reprovada',
                    alteracao: 'Alteração pedida ao Aéreo',
                    confirmar: 'Alteração aprovada',
                    solicitarEventos: 'Malha enviada para a aprovação de Eventos',
                    notificar: 'Alteração notificada',
                };
                toast.success(rotulo[acao] ?? 'Decisão registrada');
                // O back devolve o estado já recalculado: situação e botões.
                setAprovacao(res.data.data?.aprovacao ?? null);
                setObservacao('');
                if (acao === 'enviar' && headerEvento.solicitacao) {
                    voltarAoPainel = true;
                }
            })
            .catch(err => notify.apiError(err, 'Erro ao registrar a decisão'))
            .finally(() => {
                setDecidindo(false);
                setConfirmacao(null);
                if (voltarAoPainel) irParaMalhas();
            });
    };

    // A observação é escrita DENTRO da confirmação, então começa limpa a cada abertura — nunca
    // herda a de uma decisão anterior.
    const abrirConfirmacao = (tipo) => {
        setObservacao('');
        setConfirmacao(tipo);
    };

    // Malha alterada: quem aceita a alteração é o próprio RSVP, e é no Importar que ele aceita. A
    // confirmação vai antes, e a Importação grava em seguida — com a mesma seleção.
    const aprovarEImportar = () => {
        setImportando(true);
        api({ method: 'post', action: '/eventoMalhaAereaAprovacao', token, data: { malha: idMalha, acao: 'confirmar' } })
            .then(res => {
                setAprovacao(res.data.data?.aprovacao ?? null);
                importar();
            })
            .catch(err => {
                notify.apiError(err, 'Erro ao aprovar a alteração');
                setImportando(false);
                setConfirmacao(null);
            });
    };

    const confirmarAcao = () => {
        if (confirmacao === 'enviar') return enviarParaAprovacao();
        if (confirmacao === 'notificar') return notificarAlteracao();
        if (confirmacao === 'importar') return aprovacao?.importar?.alterada ? aprovarEImportar() : importar();
        if (confirmacao === 'revisarAlteracao') return decidir('confirmar');
        return decidir(confirmacao);
    };

    // Enviar para aprovação SALVA antes, como o Exportar: quem aprova julga o que está
    // gravado, e enviar sem gravar mandaria a versão de antes da edição. Se o Salvar
    // falhar, não envia.
    const enviarParaAprovacao = async () => {
        setDecidindo(true);
        try {
            await persistir();
        } catch (err) {
            notify.apiError(err, 'Erro ao salvar a malha — ela não foi enviada');
            setDecidindo(false);
            setConfirmacao(null);
            return;
        }
        decidir('enviar');
    };

    // Notificar SALVA antes, pela mesma razão do Enviar: a notificação diz "a malha mudou", e
    // mandá-la com a edição ainda só na tela apontaria quem recebe para a versão de antes.
    // Falhando o Salvar, não notifica — senão o e-mail sai e a mudança não existe.
    const notificarAlteracao = async () => {
        setDecidindo(true);
        try {
            await persistir();
        } catch (err) {
            notify.apiError(err, 'Erro ao salvar a malha — a notificação não foi enviada');
            setDecidindo(false);
            setConfirmacao(null);
            return;
        }
        decidir('notificar');
    };

    const salvar = () => {
        setSalvando(true);
        persistir()
            // Chega aqui só quando o PUT falhou — aí a malha realmente não salvou.
            // apiError mostra o que a API respondeu; o toast fixo escondia a causa.
            .catch(err => notify.apiError(err, 'Erro ao salvar a malha'))
            .finally(() => setSalvando(false));
    };

    // A resposta do export vem como Blob (responseType), então o erro da API também —
    // é preciso ler o Blob como texto e desempacotar o JSON para ver a mensagem real.
    const mensagemErroExport = async (error) => {
        try {
            const data = error?.response?.data;
            if (data instanceof Blob) {
                const texto = await data.text();
                if (texto) {
                    try {
                        const json = JSON.parse(texto);
                        return json?.error || json?.msg || json?.message || 'Erro ao exportar a malha.';
                    } catch (e) {
                        return texto;
                    }
                }
            }
        } catch (e) { /* cai na mensagem padrão */ }
        return 'Erro ao exportar a malha.';
    };

    // Baixa o .xlsx montado a partir do estado da TELA (POST com o mesmo payload do Salvar).
    // Nada é gravado: quem simula um agrupamento, e até quem não edita a malha, baixa o que vê.
    const baixar = async () => {
        const { opcoes: opcoesTela, cabecalho: cabecalhoTela, ordemTrechos } = payloadMalha();
        const response = await axios.post(`${process.env.REACT_APP_API_URL}/eventoAereoMalhaOpcaoExportar`, {
            malha: idMalha,
            // O rascunho não tem malha gravada: a planilha sai só da tela.
            rascunho: rascunho || undefined,
            opcoes: opcoesTela,
            cabecalho: cabecalhoTela,
            ordemVinculos: ordemTrechos,
        }, {
            headers: {
                Accept: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
                Authorization: `Bearer ${token}`,
            },
            responseType: 'blob',
        });

        const blob = new Blob([response.data], {
            type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        });
        const nomeArquivo = nomeDoArquivo(response.headers?.['content-disposition'], 'Malha Aérea.xlsx');

        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = nomeArquivo;
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(url);
    };

    // Exporta no layout "Modelo Cliente" (.xlsx) com o que está na tela, sem salvar — para
    // qualquer cargo e com a malha travada ou não. Gravar continua sendo só o Salvar.
    const exportar = async () => {
        if (opcoes.length === 0) return;
        setExportando(true);
        try {
            await baixar();
        } catch (error) {
            toast.error(await mensagemErroExport(error));
        } finally {
            setExportando(false);
        }
    };

    const enviarArquivo = async (file, tipo = null) => {
        setCarregando(true);
        // O upload grava as opções e vincula cada uma pelos grupos e edições de trecho do
        // BANCO. Os que estão só na tela (criados ou editados sem Salvar) ficariam fora do
        // vínculo automático, e a opção ligada a um grupo ainda sem id real perderia o
        // vínculo na gravação. Por isso a tela é salva antes — só na primeira chamada: a
        // segunda (tipo escolhido no modal) chega sem mudança no meio.
        let existentes = opcoesRef.current;
        if (!tipo) {
            try {
                const res = await persistir({ silencioso: true });
                const salvas = res?.data?.data?.opcoes;
                if (Array.isArray(salvas)) existentes = prepara(salvas);
            } catch (err) {
                notify.apiError(err, 'Erro ao salvar a malha antes do upload — o arquivo não foi importado');
                setCarregando(false);
                return;
            }
        }
        api({
            method: 'post',
            action: '/eventoAereoMalhaOpcaoParsear',
            token,
            // As opções vão como UM campo JSON: o arquivo faz o envio ser multipart, onde cada
            // campo de cada opção contava como uma variável, e o PHP corta em 1000
            // (max_input_vars). Numa malha grande o fim da lista chegava truncado, e o back
            // apagava as opções que faltavam.
            data: { file, malha: idMalha, tipo, opcoesExistentes: JSON.stringify(existentes) },
        }).then(response => {
            const payload = response.data.data ?? {};
            if (payload.precisaTipo) {
                setArquivoPendente(file);
                setSugestaoTipo(payload.sugestaoTipo ?? null);   // pré-seleciona no modal
                setSugestaoMotivo(payload.sugestaoMotivo ?? '');
                setPrecisaTipo(true);
                return;
            }
            // O backend já mescla e persiste — substitui pelo resultado retornado. No rascunho ele só mescla: a linha
            // solta vai para o grupo da cidade dela, como o back faz na sugestão, e só o Salvar guarda.
            const linhas = Array.isArray(payload.rows) ? payload.rows : [];
            if (rascunho) {
                const agrupadas = vincularPorCidade(linhas, trechos, idTemporario);
                if (agrupadas.novos.length) setTrechos(prev => [...prev, ...agrupadas.novos]);
                setOpcoes(prepara(agrupadas.opcoes));
                toast.success('Planilha lida — clique em Salvar para guardar neste navegador');
                return;
            }
            setOpcoes(prepara(linhas));
            const hist = payload.arquivos ?? [];
            setArquivosHist(Array.isArray(hist) ? hist : []); // histórico atualizado vem na resposta
            // Na sugestão, o upload cria o grupo de cada cidade nova: o painel volta com eles.
            if (Array.isArray(payload.trechos)) setTrechos(payload.trechos);
            marcarGravado();
            // As travas foram lidas no Salvar de antes do upload, com a malha ainda sem cotação: o
            // Enviar só aparece relendo depois que as opções gravaram.
            relerTravas();
        // apiError mostra a recusa do back (ex.: opção importada que a planilha tiraria).
        }).catch(err => notify.apiError(err, 'Erro ao processar o arquivo'))
          .finally(() => setCarregando(false));
    };

    const handleArquivo = (file) => {
        setArquivo(file);
        enviarArquivo(file);
    };

    const escolherTipo = (tipo) => {
        setPrecisaTipo(false);
        setSugestaoTipo(null);
        setSugestaoMotivo('');
        if (arquivoPendente) enviarArquivo(arquivoPendente, tipo);
    };

    // Escreve `field` em uma ou mais opções (índices na lista `opcoes`). Numa célula
    // mesclada, todos os `targets` mudam juntos.
    const updateOpcaoFields = (indices, field, value) =>
        setOpcoes(prev => prev.map((op, i) => indices.includes(i) ? { ...op, [field]: value } : op));

    // Vários campos em várias linhas de uma vez: o destaque e o colar do clique direito na planilha.
    // O colar traz tarifa e taxas do lado copiado, e o total da linha acompanha.
    const alterarOpcoes = (mudancas) => setOpcoes(prev => {
        const porIndice = new Map(mudancas.map(m => [m.i, m.campos]));
        return prev.map((op, i) => {
            const campos = porIndice.get(i);
            if (!campos) return op;
            const nova = { ...op, ...campos };
            return ['tarifaIda', 'taxasIda', 'tarifaRet', 'taxasRet'].some(c => c in campos)
                ? { ...nova, total: calcularTotal(nova) }
                : nova;
        });
    });

    // Edição de coluna mesclada. Igual a updateOpcaoFields, mas ao ESVAZIAR uma run
    // (uma ou várias células de mesmo valor) ela adota na hora o valor comum do que
    // SOBROU no grupo e junta — sem esperar o Salvar. Se não sobrou nada (limpou o grupo
    // inteiro), fica vazia p/ redigitar.
    //
    // Na OBS e no Num. Part., o modal do grupo é MAIS UMA LINHA (`valorDoModal`): no run editado
    // ele muda junto; fora dele, vota no comum do que sobrou. Na origem também: ela se edita como o
    // trecho, e a edição vale só nesta malha. Quem edita o próprio modal (`salvarEdicao`) passa o
    // vínculo e `comModal`.
    const editarMerge = (indices, field, value, vinculo = null, comModal = null) => {
        const sid = vinculo ?? (indices.length ? vinculoDe(opcoes[indices[0]]) : null);
        const doGrupo = sid == null ? [] : opcoes.map((o, i) => i).filter(i => String(vinculoDe(opcoes[i])) === String(sid));
        const modal = valorDoModal(sid, field, trechos);
        const noModal = modal !== null;
        const junto = noModal && (comModal ?? linhasNoRunDoModal(doGrupo.map(i => opcoes[i][field]), modal)
            .map(k => doGrupo[k]).includes(indices[0]));
        // O modal depois desta edição: esvaziado no run, fica o que ele herda (a origem, no Num.
        // Part.); fora do run, o valor dele.
        const herdado = junto ? valorDoModal(sid, field, trechos, { herdada: true }) : '';
        const voto = (junto ? herdado : (modal ?? '')) ? [junto ? herdado : modal] : [];
        // valorComum ignora vazios → pega o mais frequente do que restou no grupo; no empate,
        // o do modal.
        const final = value !== '' || sid == null ? value : valorComum([
            ...doGrupo.filter(i => !indices.includes(i)).map(i => opcoes[i][field]),
            ...voto,
        ], voto[0]);
        setOpcoes(prev => prev.map((op, i) => indices.includes(i) ? { ...op, [field]: final } : op));
        // Esvaziado, o modal adota o comum, como a linha — a não ser que herde: aí fica vazio e vale
        // a herança.
        if (junto) escreverNoVinculo(sid, field, value !== '' || herdado ? value : final);
    };

    // O valor no vínculo — o que o modal do grupo mostra: a observação, ou os passageiros (vazio
    // grava NULL, que é o "vale a origem"). Grupo e edição mudam a própria linha; trecho ou origem
    // intactos viram editados, valendo só nesta malha, como o lápis faz. Só state; grava no Salvar.
    const escreverNoVinculo = (sid, field, valor) => {
        const chave = field === 'obs' ? 'observacao' : 'quantidadePassageiros';
        const v = field === 'obs' ? valor : (String(valor ?? '') === '' ? null : Number(valor));
        setTrechos(prev => prev.map(s => {
            if (String(s.id) !== String(sid)) return s;
            const sobrescrita = { ...(s.sobrescrita ?? {}), [chave]: v };
            if (!intacto(s)) return { ...s, [chave]: v, sobrescrita };
            // `original` guarda o valor de antes — é o que o Reverter restaura.
            const original = CAMPOS_GRUPO.reduce((acc, k) => ({ ...acc, [k]: s[k] ?? null }), {});
            return { ...s, [chave]: v, sobrescrita, base: baseDoVinculo(s),
                tipo: 'editada', grupo: idTemporario(), original };
        }));
    };

    // Abre o modal do vínculo. Vazio abre com o comum das opções dele: o modal é mais uma linha da
    // OBS e do Num. Part., e linha vazia adota o comum. Aplicar o modal grava esse valor nele. No
    // Num. Part., com a herança da origem o modal não está vazio: vale ela.
    const abrirModal = (s) => {
        const doGrupo = opcoes.filter(o => String(vinculoDe(o)) === String(s?.id));
        const comum = (field) => (valorDoModal(s?.id, field, trechos) ? '' : valorComum(doGrupo.map(o => o[field])));
        const obs = comum('obs');
        const pax = comum('numParticipantes');
        setItemEditando({ ...s, ...(obs ? { observacao: obs } : {}), ...(pax ? { quantidadePassageiros: Number(pax) } : {}) });
    };

    // Validação viva (status por opção + resumo por trecho) para badges/cores.
    const validacao = useMemo(() => validar(opcoes, trechos), [opcoes, trechos]);

    // Dois cliques no painel de trechos: a planilha rola até as opções da linha e elas piscam.
    const focarNaMalha = (ids) => {
        if (!ids.length) {
            toast.info('Nenhuma opção na malha para esta linha.');
            return;
        }
        setFocoMalha({ ids, vez: Date.now() });
    };

    // Drag no board (mover entre grupos + reordenar dentro): o board emite o layout
    // final { grupoId: [opId] }. Reconstrói as opções nessa ordem, fixa o trecho
    // de cada grupo ('__orfas__' = sem) e renumera por grupo → a coluna "Opção" reflete
    // a posição no grupo.
    const reorganizar = (mapaIdsPorGrupo) =>
        setOpcoes(prev => {
            const byId = new Map(prev.map(op => [String(op._id), op]));
            const novas = [];
            Object.entries(mapaIdsPorGrupo).forEach(([grupoId, ids]) => {
                // Grava a coluna do que o vínculo é (grupo, trecho ou origem) e zera as outras:
                // o vínculo é exclusivo.
                const vinculo = grupoId === '__orfas__' ? null : trechos.find(s => String(s.id) === String(grupoId));
                ids.forEach(id => {
                    const op = byId.get(String(id));
                    if (op) { novas.push(comVinculo(op, vinculo ?? null)); byId.delete(String(id)); }
                });
            });
            byId.forEach(op => novas.push(op)); // segurança: nenhuma opção se perde
            return numerarPorGrupo(novas);
        });

    // X no chip do accordion: remove a opção inteira da tabela (renumera por grupo).
    // Opção importada não sai da malha (trava do back): recusa aqui, na hora, em vez de a
    // remoção local só estourar no Salvar, quando já não se sabe qual linha foi.
    const motivoNaoRemover = (op) => (op?.id && portadasIds.has(String(op.id))
        ? 'Esta opção está nas opções do evento e não sai da malha. O RSVP a retira na Importação das Opções de Aéreo antes.'
        : null);
    const removerOpcao = (itemId) => {
        const motivo = motivoNaoRemover(opcoes.find(op => String(op._id) === String(itemId)));
        if (motivo) {
            toast.warning(motivo);
            return;
        }
        setOpcoes(prev => numerarPorGrupo(prev.filter(op => String(op._id) !== String(itemId))));
    };
    // As linhas marcadas na planilha (clique na rota): saem juntas, como o X do chip, e só da tela até o
    // Salvar. A que está no evento nem se marca (`motivoNaoRemover`).
    const removerOpcoes = (ids) => {
        const fora = new Set(ids.map(String));
        setOpcoes(prev => numerarPorGrupo(prev.filter(op => !fora.has(String(op._id)))));
        toast.success(`${ids.length} ${ids.length === 1 ? 'linha excluída' : 'linhas excluídas'} — clique em Salvar para gravar`);
    };

    // Linhas que não vieram na planilha: a da Nova linha, que o modal monta com o voo, e as do Duplicar do clique
    // direito, que a planilha monta da cópia. Passam pelo vínculo automático, o mesmo do "Vincular órfãs" e do upload;
    // sem casamento, ficam órfãs e se relacionam no board. Ficam só no state; o Salvar cria de fato (linha sem `id` =
    // insert no upsert do backend).
    const adicionarLinhas = (novas) => {
        // Sem solicitação, a linha vai para o grupo da cidade e do destino dela, criado quando falta, como no upload.
        const { opcoes: ligadas, novos = [], vinculadas } = semPedido
            ? vincularPorCidade(novas, trechos, idTemporario)
            : vincularOrfas(novas, trechos);
        if (novos.length) setTrechos(prev => [...prev, ...novos]);
        setOpcoes(prev => prepara([...prev, ...ligadas]));
        setNovaLinhaAberta(false);
        const texto = novas.length === 1
            ? (semPedido
                ? (vinculadas ? 'Linha adicionada no grupo da cidade dela' : 'Linha adicionada nas órfãs: sem cidade para agrupar')
                : (vinculadas ? 'Linha adicionada e relacionada automaticamente' : 'Linha adicionada nas órfãs: nenhum trecho casou'))
            : `${novas.length} linhas adicionadas${vinculadas
                ? `, ${vinculadas} ${semPedido ? 'nos grupos das cidades' : 'relacionadas automaticamente'}` : ' nas órfãs'}`;
        toast.success(texto + ' — clique em Salvar para gravar');
    };

    // Relaciona automaticamente as opções órfãs ao trecho correspondente, com a
    // mesma âncora do import (origem E destino; sem vínculo assim, uma das pontas).
    // Não toca no que já está relacionado — vínculo feito na mão continua valendo.
    // Só state; sobe no Salvar.
    const vincularAutomatico = () => {
        const atuais = opcoesRef.current;
        // Sem solicitação, cada órfã vai para o grupo da cidade dela, e a cidade sem grupo ganha um, como no upload.
        if (semPedido) {
            const { opcoes: agrupadas, novos, vinculadas: n } = vincularPorCidade(atuais, trechos, idTemporario);
            if (!n) {
                toast.info('Nenhuma órfã com cidade para agrupar.');
                return;
            }
            if (novos.length) setTrechos(prev => [...prev, ...novos]);
            setOpcoes(numerarPorGrupo(agrupadas));
            toast.success(`${n} ${n === 1 ? 'opção agrupada' : 'opções agrupadas'}${novos.length
                ? ` e ${novos.length} ${novos.length === 1 ? 'grupo novo' : 'grupos novos'}` : ''}: clique em Salvar para gravar.`);
            return;
        }
        const { opcoes: novas, vinculadas } = vincularOrfas(atuais, trechos);
        // Vínculo pendurado sem casamento também sai, então aplica mesmo sem nenhuma vinculada.
        if (novas.some((op, i) => op !== atuais[i])) setOpcoes(numerarPorGrupo(novas));
        if (!vinculadas) {
            toast.info('Nenhuma órfã casou com um trecho.');
            return;
        }
        toast.success(`${vinculadas} ${vinculadas === 1 ? 'opção relacionada' : 'opções relacionadas'}.`);
    };

    // ── Grupos do Aéreo ──────────────────────────────────────────────────────
    // Tudo em state; nada vai ao banco antes do Salvar — criar um grupo e desistir
    // dele não deixa linha nenhuma para trás. O `persistir` manda a lista inteira e
    // o back apaga o que sumiu, mesmo contrato dos trechos do trecho.
    // Gravam SÓ em `eventomalhagrupo`; o trecho do cliente é intocável.
    const idTemporario = () => `novo-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`;

    // Lápis: trecho ou origem intactos viram "editada" (sobrescreve os valores sem tocar no pedido,
    // só nesta malha); editada e grupo atualizam a própria linha.
    const salvarEdicao = (campos) => {
        const alvo = itemEditando;
        // Grupo que ainda não existe: é este Salvar que o cria. Cancelar, antes daqui, não deixa
        // rastro nenhum.
        if (alvo?._novo) {
            const { _novo, ...base } = alvo;
            setTrechos(prev => [...prev, { ...base, ...campos }]);
            setItemEditando(null);
            toast.success('Grupo criado — clique em Salvar para gravar');
            return;
        }
        // O modal devolve o trecho INTEIRO, como a tela de solicitação faz — ele não sabe o que é
        // sobrescrita. Quem sabe é aqui: campo igual ao do trecho por baixo NÃO é edição, e volta a
        // vazio para gravar NULL ("não sobrescreve"). Sem isto, abrir e salvar o modal congelaria
        // como próprio tudo o que o trecho só herdava — e a origem, mudando depois, não chegaria
        // mais nele.
        const base = alvo?.valoresDoTrecho ?? null;
        const soAsEdicoes = !base ? campos : Object.fromEntries(Object.entries(campos).map(([k, v]) => {
            const igual = String(v ?? '').trim() === String(base[k] ?? '').trim();
            return [k, k in base && igual ? null : v];
        }));
        // Os valores EFETIVOS ficam no topo (é o que o painel e a tabela mostram); o que de fato é
        // sobrescrita vai à parte, e é ela que sobe no Salvar.
        setTrechos(prev => prev.map(s => {
            if (String(s.id) !== String(alvo?.id)) return s;
            if (!intacto(s)) return { ...s, ...campos, sobrescrita: soAsEdicoes };
            // `original` guarda o valor de antes — é o que o Reverter restaura.
            const original = CAMPOS_GRUPO.reduce((acc, k) => ({ ...acc, [k]: s[k] ?? null }), {});
            return { ...s, ...campos, sobrescrita: soAsEdicoes, base: baseDoVinculo(s),
                tipo: 'editada', grupo: idTemporario(), original };
        }));
        // O modal é mais uma linha da OBS e do Num. Part.: mudou, ele passa pelo `editarMerge` com
        // as linhas do run dele. Depois do setTrechos acima, para o que o merge decide ser a última
        // palavra (esvaziado, ele adota o comum — ou, no Num. Part., volta a valer a origem).
        [['obs', 'observacao'], ['numParticipantes', 'quantidadePassageiros']].forEach(([field, chave]) => {
            if (!(chave in campos) || String(campos[chave] ?? '') === String(alvo?.[chave] ?? '')) return;
            const sid = String(alvo?.id);
            const doGrupo = opcoes.map((o, i) => i).filter(i => String(vinculoDe(opcoes[i])) === sid);
            const alvos = linhasNoRunDoModal(doGrupo.map(i => opcoes[i][field]), valorDoModal(sid, field, trechos))
                .map(k => doGrupo[k]);
            editarMerge(alvos, field, String(campos[chave] ?? ''), sid, true);
        });
        setItemEditando(null);
        toast.success('Alterações aplicadas — clique em Salvar para gravar');
    };

    // Reverter (editada) devolve os valores de origem; Excluir (grupo) tira a linha
    // e devolve as opções dela para as órfãs.
    const removerEdicao = (vinculo = null) => {
        // Vem do rodapé do modal (sem argumento, é o que está aberto) ou do botão na linha do
        // painel, que age direto no vínculo. A regra é a mesma nos dois.
        const alvo = vinculo ?? itemEditando;
        if (alvo?.tipo === 'grupo') {
            setTrechos(prev => prev.filter(s => String(s.id) !== String(alvo.id)));
            setOpcoes(prev => prev.map(op => (String(op.grupo) === String(alvo.id) ? { ...op, grupo: null } : op)));
        } else {
            setTrechos(prev => prev.map(s => (String(s.id) === String(alvo?.id)
                ? { ...s, ...(s.original ?? {}), tipo: baseDoVinculo(s), grupo: null, original: null }
                : s)));
        }
        setItemEditando(null);
        toast.success(alvo?.tipo === 'grupo' ? 'Grupo excluído' : (baseDoVinculo(alvo) === 'origem' ? 'Origem revertida' : 'Trecho revertido'));
    };

    // Atalho para a Solicitação do mesmo evento. SEM aviso de trabalho não salvo: ele
    // cobria só este botão, e sair pelo menu, pelo voltar do navegador ou fechando a aba
    // descartava calado do mesmo jeito. Guarda que protege uma saída de seis dá falsa
    // segurança. Quando o aviso voltar, o lugar dele é a saída da TELA — `beforeunload`
    // mais o `Prompt` do router — comparando o estado atual com o último carregado.
    // Sobe UM nível: o painel de Malhas da própria solicitação. Não vai direto ao
    // painel de Solicitações, que é por evento — daqui o pedido é o contexto.
    // O pedido chega no cabeçalho: esta tela só conhece a malha, e o painel de Malhas
    // é endereçado pela solicitação.
    const irParaMalhas = () => history.push('/cadastroEventoMalhasAereo/' + headerEvento.solicitacao);

    // Grupo novo: abre o modal e NÃO entra na lista ainda — quem o cria é o Salvar dele (`_novo`).
    // Entrando antes, cancelar deixava para trás um grupo vazio, que não casa com opção nenhuma e
    // ainda ocupa uma linha do painel. O id temporário já nasce aqui porque é ele que amarra o
    // grupo às opções; o back devolve o real ao gravar.
    const adicionarGrupo = () => {
        const id = idTemporario();
        // Nasce ida e volta, como o trecho da solicitação. Leva o destino do pedido como referência,
        // como o back manda depois do Salvar: sem ele, o destino com o badge ligado não seria
        // próprio, e o "Vincular órfãs" decidiria diferente antes e depois de gravar.
        setItemEditando({ id, grupo: id, tipo: 'grupo', ordem: trechos.length, ida: true, retorno: true,
            destinoPedido: pedido?.destino ?? null, _novo: true });
    };

    // Reordena os trechos (muda SÓ a ordem) → reordena os grupos da tabela,
    // que são montados na ordem de `trechos`. Só no state; persiste ao Salvar.
    const reordenarTrechos = (idsOrdenados) =>
        setTrechos(prev => {
            const byId = new Map(prev.map(s => [String(s.id), s]));
            const ordenadas = idsOrdenados.map(id => byId.get(String(id))).filter(Boolean);
            prev.forEach(s => { if (!idsOrdenados.some(id => String(id) === String(s.id))) ordenadas.push(s); });
            return ordenadas;
        });

    // ── Importação das opções ────────────────────────────────────────────────
    // Só com a malha aprovada e para o RSVP (`importar.pode`, do back). Sem aviso a ninguém:
    // quem importa escolhe o que vai para o evento. A prévia é a da tela de Importação do
    // evento; daqui se usa só o bloco desta malha.
    // Duas coisas diferentes, e misturá-las era o que fazia o seletor do admin demorar:
    //
    // `temImportacao` é a PERMISSÃO, do back — é ela que manda buscar a prévia. Presa à visão, a
    // troca para Aéreo jogava a prévia fora e a volta para RSVP refazia o GET do evento inteiro;
    // até ele responder, as colunas de importar não existiam, e a planilha ainda remedia tudo.
    //
    // `podeImportar` é a VISÃO: é ela que decide se a coluna aparece. Trocar de visão agora é só
    // re-render, sem ida ao servidor — o dado já está na mão.
    const temImportacao = !!aprovacao?.importar?.pode;
    const podeImportar = acaoDoRsvp(aprovacao?.importar?.pode);
    const idEvento = headerEvento.evento;

    const carregarImportacao = useCallback(() => {
        if (!temImportacao || !idEvento) {
            setPreviaMalha(null);
            return;
        }
        api({ method: 'get', action: '/eventoAereoPortacao', token, params: { evento: idEvento } })
            .then(res => {
                const bloco = (res.data.data?.malhas ?? []).find(m => String(m.id) === String(idMalha));
                setPreviaMalha(bloco ?? null);
            })
            .catch(err => {
                setPreviaMalha(null);
                notify.apiError(err, 'Erro ao carregar o que já foi importado desta malha');
            });
    }, [temImportacao, idEvento, idMalha, token]);

    useEffect(() => {
        setMarcados(lerSelecaoDaMalha(idMalha));
        carregarImportacao();
    }, [carregarImportacao, idMalha]);

    // A seleção fica guardada por malha até a importação gravar ou ela ser desmarcada. Só quem
    // importa grava: a tela de quem não importa não mexe no que o RSVP marcou.
    useEffect(() => {
        if (temImportacao) guardarSelecaoDaMalha(idMalha, marcados);
    }, [temImportacao, idMalha, marcados]);

    // Opção da prévia pelo id da opção da malha.
    const infoImportacao = useMemo(
        () => new Map((previaMalha?.opcoes ?? []).map(o => [String(o.malhaOpcao), o])),
        [previaMalha]
    );

    // A coluna "Importar" da planilha: os lados de cada opção, marcados ou não. A regra é a mesma
    // da Importação das Opções, e mora com ela em `util/malhaAereo`.
    const selecaoImportacao = useMemo(
        () => (podeImportar && previaMalha
            ? selecaoDeImportacao({ opcoes, infoPorOpcao: infoImportacao, marcados, setMarcados })
            : null),
        [podeImportar, previaMalha, infoImportacao, marcados, opcoes],
    );

    const acoesImportar = useMemo(() => acoesDeImportacao(marcados, infoImportacao), [marcados, infoImportacao]);
    const ladosMarcados = contarLadosMarcados(acoesImportar);

    const importar = () => {
        const n = acoesImportar.length;
        setImportando(true);
        api({ method: 'post', action: '/eventoAereoPortacao', token, data: { evento: idEvento, acoes: acoesImportar } })
            .then(res => {
                const bloco = (res.data.data?.malhas ?? []).find(m => String(m.id) === String(idMalha));
                setPreviaMalha(bloco ?? null);
                setMarcados({});
                toast.success(n === 1 ? 'Opção importada para o evento' : `${n} opções importadas para o evento`);
                // A contagem "no evento" do rodapé vem do estado da aprovação. Só ele é relido:
                // trocar as opções desfaria o agrupamento simulado na tela.
                return api({ method: 'get', action: '/eventoAereoMalhaOpcao', token, params: { malha: idMalha } })
                    .then(r => setAprovacao(r.data.data?.aprovacao ?? null))
                    .catch(() => {});
            })
            .catch(err => notify.apiError(err, 'Erro ao importar as opções'))
            .finally(() => {
                setImportando(false);
                setConfirmacao(null);
            });
    };

    const grupo = aprovacao?.grupo;
    const admin = grupo === 'admin';
    // Vindo do back, o admin pode aprovar de qualquer visão — a permissão não muda. O que muda é a
    // tela: olhando como Aéreo, os botões de Eventos não são dele agora.
    const decideEventos = !!(aprovacao?.aprovar?.pode || aprovacao?.reprovar?.pode)
        && (!ehAdmin || visaoAdmin === 'eventos');
    const revisaAlteracao = acaoDoRsvp(aprovacao?.confirmarAlteracao?.pode)
        || acaoDoRsvp(aprovacao?.solicitarEventos?.pode);
    const outrasAcoes = decideEventos || revisaAlteracao
        || acaoDoRsvp(aprovacao?.pedirAlteracao?.pode) || !!selecaoImportacao;

    // O Salvar do rodapé do Aéreo, que é também o rodapé inteiro do rascunho.
    const botaoSalvar = (
        <button type="button" onClick={salvar}
            // `editar.pode` vem do back: aprovada, alterada e cancelada não gravam.
            disabled={salvando || exportando || carregando || buscando || aprovacao?.editar?.pode === false}
            title={aprovacao?.editar?.motivo || undefined}
            className={'fin-pp__btn fin-pp__btn--tall' + (aprovacao?.enviar?.pode ? '' : ' fin-pp__btn--primary')}>
            {salvando
                ? <span className="spinner-border spinner-border-sm" role="status" />
                : <FaSave size={16} />}
            {salvando ? 'Salvando...' : 'Salvar'}
        </button>
    );

    return (
        // Com a planilha na tela, o espaço do rodapé fixo quem reserva é ela, medindo o rodapé
        // de verdade — os 100px chutados do template sobrariam como rolagem e abririam um vão
        // embaixo da tabela. Sem planilha (nada importado ainda), vale o padrão.
        <ErpTemplate espacoFinal={opcoes.length > 0 ? 0 : 100}>
            <div className="main-content container-fluid fin-pp">

                {/* Cabeçalho enxuto do evento */}
                <CabecalhoEventoAereo
                    titulo={rascunho ? 'Rascunho de Malha' : (sugestao ? 'Sugestão de Voos' : 'Importação Malha Aérea')}
                    nomeEvento={headerEvento.nomeEvento || cabecalho.nomeEvento}
                    cliente={headerEvento.cliente || cabecalho.cliente}
                    solicitante={headerEvento.solicitante || (!PERFIS_AEREO.includes(papel) ? nomeUsuario : '')}
                    dataEvento={headerEvento.dataEvento}
                    localEvento={headerEvento.localEvento}
                    tktMedio={headerEvento.tktMedio ?? null}
                    // Os vínculos da malha por base: origem sem trecho, trecho e grupo do Aéreo.
                    voosSolicitados={[
                        ['origem', 'origem', 'origens'],
                        ['trecho', 'trecho', 'trechos'],
                        ['grupo', 'grupo', 'grupos'],
                    ].map(([base, um, varios]) => {
                        const n = trechos.filter(s => baseDoVinculo(s) === base).length;
                        return n ? `${n} ${n !== 1 ? varios : um}` : null;
                    }).filter(Boolean).join(' · ') || (semPedido ? '0 grupos' : '0 trechos')}
                    // A sugestão volta à emissão: a do Aéreo, ou a do RSVP com a Importação das Opções.
                    atalhos={sugestao ? [
                        atendimento.atalho,
                        PERFIS_AEREO.includes(papel)
                            ? { rotulo: 'Emissão de Aéreo', titulo: 'Volta à emissão do evento.', to: '/cadastroEventoEmissaoAereo/' + sugestao.idEvento }
                            : { rotulo: 'Solicitar Emissão de Aéreo', titulo: 'Volta à emissão do evento.', to: '/cadastroEventoEmissaoEnvioAereo/' + sugestao.idEvento },
                        abreImportacao && {
                            rotulo: 'Importação das Opções',
                            titulo: 'Importação das Opções de Aéreo do evento: as malhas aprovadas, a sugestão e o que já está no evento.',
                            to: '/cadastroEventoPortacaoAereo/' + sugestao.idEvento,
                        },
                    ] : headerEvento.solicitacao ? [
                        atendimento.atalho,
                        { rotulo: 'Solicitações', titulo: 'Painel de Solicitações deste evento.', to: '/cadastroEventoSolicitacoesAereo/' + headerEvento.evento },
                        { rotulo: 'Solicitação', titulo: 'Origens, destino, datas e trechos desta solicitação.', to: '/cadastroEventoSolicitacaoAereo/' + headerEvento.solicitacao },
                        { rotulo: 'Malhas', titulo: 'Volta ao painel de Malhas desta solicitação.', to: '/cadastroEventoMalhasAereo/' + headerEvento.solicitacao },
                        abreImportacao && headerEvento.evento && {
                            rotulo: 'Importação das Opções',
                            titulo: 'Importação das Opções de Aéreo do evento: as malhas aprovadas e o que já está no evento.',
                            to: '/cadastroEventoPortacaoAereo/' + headerEvento.evento,
                        },
                    ] : []}
                />

                {/* Leitura recusada (rascunho do Aéreo aberto por outro cargo) ou falha: o motivo em
                    vez de uma malha vazia, que pareceria "sem cotação". */}
                {/* O banner do design system, não card com ícone montado à mão: é o mesmo aviso
                    que a solicitação e o financeiro usam. `is-warn` porque isto trava o upload. */}
                {bloqueioLeitura && (
                    <div className="fin-pp fin-pp__banner is-warn">
                        <FaExclamationTriangle className="fin-pp__banner-ic" size={14} />
                        <span>{bloqueioLeitura}</span>
                    </div>
                )}

                {/* Cabeçalho do Modelo Cliente. A sugestão de voos e o rascunho não têm. */}
                {!semPedido && <CabecalhoModeloCliente
                    dados={cabecalho}
                    padroes={padroes}
                    opcoesProduto={produtosDoEvento}
                    disabled={!isAereo}
                    onChange={(campo, valor) => setCabecalho(prev => ({ ...prev, [campo]: valor }))} />}

                {/* As premissas da solicitação, acima do upload: é o que a cotação tem de seguir. Para todo cargo. */}
                {String(pedido?.observacao ?? '').trim() && (
                    <div className="fin-pp__card mb-4" style={{ padding: 16 }}>
                        <div className="fin-pp__cphead">
                            <span className="fin-pp__cphead-ic"><FaInfoCircle size={18} /></span>
                            <div><h2 className="fin-pp__cphead-title">Observações e Premissas de Aéreo</h2></div>
                        </div>
                        <p style={{ margin: 0, whiteSpace: 'pre-wrap', color: 'var(--fin-tx)', fontSize: 14 }}>{pedido.observacao}</p>
                    </div>
                )}

                {/* Upload — só para quem monta a malha (Aéreo). Para quem só acompanha, o campo
                    desabilitado ocupava a tela sem ação possível. Com a solicitação cancelada o
                    back recusa o upload, então o campo aparece travado. */}
                {isAereo && (
                <div className="fin-pp__card mb-4" style={{ padding: 16, cursor: (carregando || buscando) ? 'wait' : 'default' }}>
                    <div style={{ pointerEvents: (carregando || buscando) ? 'none' : 'auto', opacity: (carregando || buscando) ? 0.6 : 1 }}>
                        <UploadDropzone value={arquivo} onChange={handleArquivo} centerOnDrag
                            accept=".csv,.xlsx,.xls,.xltx,.xlsm"
                            title="Arraste o arquivo de cotação (CSV, XLSX ou XLTX) ou clique para selecionar"
                            disabled={!malhaLida || aprovacao?.editar?.pode === false || carregando || buscando || salvando || exportando} />
                    </div>
                    {carregando && (
                        <div className="d-flex align-items-center justify-content-center gap-2 mt-3" style={{ color: 'var(--fin-mt)', fontWeight: 600 }}>
                            <span className="spinner-border spinner-border-sm" role="status" style={{ color: 'var(--fin-ac)' }}>
                                <span className="visually-hidden">Carregando...</span>
                            </span>
                            Processando arquivo...
                        </div>
                    )}
                    {/* Sem nenhuma opção a tabela (e o "Adicionar linha" dela) não renderiza —
                        a saída manual precisa existir aqui, senão não há como criar a 1ª. */}
                    {isAereo && malhaLida && !carregando && !buscando && opcoes.length === 0 && (
                        <div className="d-flex align-items-center justify-content-center gap-2 mt-3" style={{ color: 'var(--fin-mt)', fontSize: 13 }}>
                            Sem planilha?
                            <button type="button" onClick={() => setNovaLinhaAberta(true)} className="fin-pp__btn fin-pp__btn--sm">
                                <FaPlus size={12} />
                                Adicionar linha
                            </button>
                        </div>
                    )}
                </div>
                )}

                {/* Histórico de uploads */}
                {arquivosHist.length > 0 && (
                    <div className="fin-pp__card mb-4" style={{ overflow: 'hidden' }}>
                        <div className="d-flex align-items-center gap-3"
                            style={{ padding: '12px 16px', cursor: 'pointer', borderBottom: histAberto ? '1px solid var(--fin-bd)' : 'none' }}
                            onClick={() => setHistAberto(a => !a)}>
                            <span className="fin-pp__cphead-ic"><FaHistory size={15} /></span>
                            <h2 className="fin-pp__cphead-title">Histórico de uploads</h2>
                            <span className="fin-pp__pill is-info">{arquivosHist.length}</span>
                            <span className="ms-auto" style={{ color: 'var(--fin-ft)', fontSize: 13 }}>
                                {histAberto ? 'clique para recolher' : 'clique para expandir'}
                            </span>
                            {histAberto
                                ? <FaChevronDown size={13} style={{ color: 'var(--fin-mt)' }} />
                                : <FaChevronRight size={13} style={{ color: 'var(--fin-mt)' }} />}
                        </div>
                        {histAberto && (
                            <div className="fin-pp__tblwrap">
                                <table className="fin-pp__tbl" style={{ tableLayout: 'auto' }}>
                                    <thead>
                                        <tr><th>Arquivo</th><th>Opções</th><th>Enviado por</th><th>Data</th><th>Tipo</th></tr>
                                    </thead>
                                    <tbody>
                                        {arquivosHist.map((arq, i) => {
                                            const tipo = TIPO_BADGE[arq.tipo];
                                            const ext = String(arq.nomeArquivo ?? '').split('.').pop().toLowerCase();
                                            const Icone = ext === 'csv' ? FaFileCsv : FaFileExcel;
                                            const qtd = Number(arq.qtdOpcoes ?? 0);
                                            return (
                                                <tr key={arq.id ?? i}>
                                                    <td className="fin-pp__cell-strong" style={{ wordBreak: 'break-all' }}>
                                                        <Icone size={14} style={{ marginRight: 8, color: 'var(--fin-ok)' }} />
                                                        {arq.nomeArquivo || 'arquivo'}
                                                    </td>
                                                    <td style={{ whiteSpace: 'nowrap' }}>{qtd} {qtd === 1 ? 'opção' : 'opções'}</td>
                                                    <td>{arq.usuario?.nome || '—'}</td>
                                                    <td style={{ whiteSpace: 'nowrap' }}>{arq.data ? formatarData(arq.data) : '—'}</td>
                                                    <td>
                                                        <span className={'fin-pp__minitag ' + (tipo?.tom ?? '')}>{tipo?.label ?? String(arq.tipo ?? '—')}</span>
                                                    </td>
                                                </tr>
                                            );
                                        })}
                                    </tbody>
                                </table>
                            </div>
                        )}
                    </div>
                )}

                {/* Validação / Agrupamento por trecho */}
                <PainelTrechos
                    titulo={semPedido ? 'Grupos' : undefined}
                    pedido={pedido}
                    trechos={trechos}
                    opcoes={opcoes}
                    validacao={validacao}
                    aberto={painelAberto}
                    onToggle={setPainelAberto}
                    // Agrupar, reordenar, vincular órfãs, editar, criar grupo e remover opção são
                    // do Aéreo (e admin): para os outros cargos o painel é só leitura, com as
                    // contagens. Quem grava continua sendo o Salvar, preso pelo `editar.pode` do back.
                    onReorganizar={isAereo ? reorganizar : undefined}
                    onRemover={isAereo ? removerOpcao : undefined}
                    onReordenar={isAereo ? reordenarTrechos : undefined}
                    onVincularOrfas={isAereo ? vincularAutomatico : undefined}
                    // Lixeira (grupo) e reverter (editada) na própria linha, sem passar pelo modal.
                    onDesfazerVinculo={isAereo ? removerEdicao : undefined}
                    onEditar={isAereo ? abrirModal : undefined}
                    onAdicionarGrupo={isAereo ? adicionarGrupo : undefined}
                    // Focar só lê: vale para todo cargo que vê a planilha.
                    onFocar={focarNaMalha}
                />

                <GrupoMalhaModal
                    aberto={!!itemEditando}
                    item={itemEditando}
                    /* As origens do pedido, para o modal poder repontar o trecho. Saem dos próprios
                       vínculos: a origem é uma linha do painel como as outras. */
                    origens={trechos.filter(s => baseDoVinculo(s) === 'origem')
                        .map(o => ({ chave: String(o.id), nome: o.origem }))}
                    // Destino e datas do pedido: é de onde o GRUPO herda. Ele não passa pelo back
                    // antes de existir, então não tem as referências que o vínculo do trecho traz.
                    pedido={pedido}
                    onFechar={() => setItemEditando(null)}
                    onSalvar={salvarEdicao}
                    // Grupo que ainda não existe não tem o que excluir: sem a ação, o rodapé fica
                    // só com Cancelar e Salvar, que é o que aquele momento oferece de verdade.
                    onRemover={itemEditando?._novo ? undefined : removerEdicao}
                />

                {/* A planilha — a mesma da Importação das Opções de Aéreo. Com a malha aprovada,
                    o RSVP marca nela a ida e a volta que vão para o evento. */}
                {opcoes.length > 0 && (
                    <PlanilhaMalhaAerea
                        titulo="Cotações Importadas"
                        opcoes={opcoes}
                        trechos={trechos}
                        editavel={isAereo}
                        onEditar={updateOpcaoFields}
                        onEditarMesclado={editarMerge}
                        onAlterarOpcoes={isAereo ? alterarOpcoes : undefined}
                        onDuplicar={isAereo ? adicionarLinhas : undefined}
                        coresAtivas={painelAberto}
                        foco={focoMalha}
                        selecao={selecaoImportacao}
                        onExcluir={isAereo ? removerOpcoes : undefined}
                        motivoNaoExcluir={motivoNaoRemover}
                        importadas={portadasIds}
                        medirDeNovo={[aprovacao, isAereo, !!selecaoImportacao]}
                        acoes={(
                            <>
                                {/* O admin acumula os três grupos do fluxo, e a tela abria com tudo
                                    ligado: o upload do Aéreo, os botões de Eventos e a coluna de
                                    importar do RSVP juntos — uma tela que nenhum dos três tem, e em
                                    que nenhum dos três fluxos se lê inteiro. O seletor troca o que
                                    ele VÊ; a permissão é a mesma, e o back segue deixando o admin
                                    fazer os três. Fica na barra da planilha porque é dela que sai a
                                    diferença entre as visões — editar, aprovar ou importar. O rascunho só tem a do Aéreo. */}
                                {ehAdmin && !rascunho && (
                                    <div className="fin-pp__seg d-flex align-items-center" role="radiogroup" aria-label="Visualização da tela">
                                        <span className="fin-pp__pill is-warn" style={{ marginRight: 4 }}>admin</span>
                                        {[
                                            ['aereo', 'Aéreo', 'Ver como o Aéreo: upload, edição da planilha e envio.'],
                                            ['eventos', 'Eventos', 'Ver como Eventos: aprovar ou reprovar a malha enviada.'],
                                            ['rsvp', 'RSVP', 'Ver como o RSVP: a coluna de importar e o atalho da Importação das Opções.'],
                                        ].map(([chave, rotulo, dica]) => (
                                            <button key={chave} type="button" title={dica}
                                                role="radio" aria-checked={visaoAdmin === chave}
                                                className={'fin-pp__seg-btn' + (visaoAdmin === chave ? ' is-active' : '')}
                                                onClick={() => setVisaoAdmin(chave)}>
                                                {rotulo}
                                            </button>
                                        ))}
                                    </div>
                                )}
                                {/* No rascunho, que só existe no navegador, a planilha sai só da tela, sem o cabeçalho do
                                    evento nem a aba Orientações. */}
                                <button type="button" onClick={exportar} disabled={exportando || salvando || opcoes.length === 0}
                                    title="Exporta a malha no layout Modelo Cliente (.xlsx) — a planilha reflete exatamente a tela."
                                    className="fin-pp__btn fin-pp__btn--sm fin-pp__btn--ok-soft">
                                    {exportando
                                        ? <span className="spinner-border spinner-border-sm" role="status" />
                                        : <FaFileExcel size={12} />}
                                    {exportando ? 'Exportando...' : 'Exportar Modelo Cliente'}
                                </button>
                                {isAereo && (
                                    <button type="button" onClick={() => setNovaLinhaAberta(true)}
                                        title="Abre o formulário do voo para adicionar uma cotação que não veio na planilha."
                                        className="fin-pp__btn fin-pp__btn--sm">
                                        <FaPlus size={12} />
                                        Adicionar linha
                                    </button>
                                )}
                                {isAereo && (
                                    <button type="button" onClick={() => setAjustarAberto(true)}
                                        className="fin-pp__btn fin-pp__btn--sm">
                                        <FaExchangeAlt size={12} />
                                        Ajustar opções (IDA &amp; RETORNO)
                                    </button>
                                )}
                            </>
                        )}
                    />
                )}

                {/* Footer — Salvar e Enviar para quem MONTA a malha (Aéreo); aprovar e reprovar
                    para Eventos; pedir alteração, aprovar a alterada e importar para o RSVP. Admin
                    vê todos. Todo "pode" e todo motivo vêm do back em `aprovacao`; a tela não
                    repete regra. Os botões são os do `.fin-pp`, como nos rodapés da solicitação e da emissão: um
                    primário por vez, e o Salvar só perde o lugar para o Enviar. */}
                {aprovacao && (isAereo || ['rsvp', 'eventos', 'admin'].includes(grupo)) && (
                    <div className="row fixoRodape">
                        <div className="col-12 d-flex align-items-center justify-content-end gap-3 flex-wrap">

                            {/* Situação e avisos — o que torna legível um botão preso. */}
                            {aprovacao.situacao && (
                                <span className="fin-pp d-inline-flex align-items-center gap-1 flex-wrap" style={{ fontSize: 12 }}>
                                    <span className={'fin-pp__pill ' + (COR_SITUACAO[aprovacao.situacao] ?? 'is-muted')}>
                                        {ROTULO_SITUACAO[aprovacao.situacao] ?? aprovacao.situacao}
                                    </span>
                                    {aprovacao.alteradoPor && (
                                        <span className="fin-pp__pill is-warn" title="Cotação mudou depois do envio. Some na próxima decisão.">
                                            Alterado pelo {aprovacao.alteradoPor}
                                        </span>
                                    )}
                                    {aprovacao.situacaoPedido === 'cancelada' && (
                                        <span className="fin-pp__pill is-danger" title="Nada mais se edita nem se decide nesta malha.">
                                            Solicitação cancelada
                                        </span>
                                    )}
                                    {aprovacao.portadas > 0 && (
                                        <span className="fin-pp__pill is-info"
                                            title="O participante já vê estas opções. Elas não saem da malha, e reprovar ou pedir alteração não as retira do evento — isso é na Importação das Opções de Aéreo.">
                                            {aprovacao.portadas} {aprovacao.portadas !== 1 ? 'opções no evento' : 'opção no evento'}
                                        </span>
                                    )}
                                </span>
                            )}

                            {/* Quem aprovou: a aprovação de Eventos continua gravada durante o
                                pedido de alteração, e é ela que o RSVP mantém ao aprovar a alterada.
                                No nacional não há aprovação, e a linha não aparece. */}
                            {aprovacao.situacao && aprovacao.situacao !== 'pendente' && aprovacao.precisaEventos && (
                                <div className="d-flex align-items-center gap-3" style={{ fontSize: 12, color: 'var(--fin-mt)' }}>
                                    <span title={aprovacao.eventosAprovouEm ? `Em ${formatarData(aprovacao.eventosAprovouEm)}` : ''}>
                                        Eventos: {aprovacao.eventosAprovou
                                            ? <strong>{aprovacao.eventosAprovouPor || 'aprovou'}</strong>
                                            : 'pendente'}
                                    </span>
                                    {aprovacao.situacao === 'aprovada' && aprovacao.aprovadaPor && aprovacao.aprovadaPor !== aprovacao.eventosAprovouPor && (
                                        <span title={aprovacao.aprovadaEm ? `Em ${formatarData(aprovacao.aprovadaEm)}` : ''}>
                                            Alteração aprovada por <strong>{aprovacao.aprovadaPor}</strong>
                                        </span>
                                    )}
                                </div>
                            )}

                            {/* Na sugestão de voos, com a tela igual ao gravado, o lugar do Salvar volta à emissão, onde
                                o Enviar para o RSVP a leva junto. */}
                            {isAereo && sugestao && semMudanca && (
                                <button type="button" onClick={() => history.push('/cadastroEventoEmissaoAereo/' + sugestao.idEvento)}
                                    title="A sugestão está salva. Na emissão, o Enviar para o RSVP a leva junto."
                                    className="fin-pp__btn fin-pp__btn--tall fin-pp__btn--primary">
                                    <FaArrowLeft size={14} />
                                    Voltar para a emissão
                                </button>
                            )}
                            {isAereo && !(sugestao && semMudanca) && botaoSalvar}

                            {/* Notificar: a malha já saiu da mão do Aéreo — está com Eventos ou já aprovada
                                — e ele mexeu nela. Sem isto o marcador de edição acendia e não tinha como
                                apagar, já que o Enviar recusa malha enviada, e quem tinha lido decidia
                                sobre uma versão que não existe mais. Na vez do Aéreo (reprovada, ou em alteração
                                pedida pelo RSVP) quem responde é o Enviar, que salva e avisa: o Notificar sai. */}
                            {isAereo && aprovacao.notificar?.pode && !aprovacao.enviar?.pode && (
                                <button type="button" onClick={() => abrirConfirmacao('notificar')}
                                    disabled={decidindo || salvando}
                                    title="A malha mudou depois de ir para frente: salva e notifica quem já a tinha lido."
                                    className="fin-pp__btn fin-pp__btn--tall">
                                    <FaExclamationTriangle size={14} />
                                    Notificar alteração
                                </button>
                            )}

                            {/* Enviar: tira a malha do rascunho e a põe na mesa de Eventos — ou, quando
                                responde a um pedido de alteração, devolve ao RSVP. Só o Aéreo: é ele que
                                monta a rodada e sabe quando ela está pronta. */}
                            {/* Some quando não dá para enviar, em vez de ficar preso: o motivo vivia
                                num `title` que ninguém abre, e a barra ficava cheia de botão morto.
                                Quem pode enviar vê; quem não pode não tem o que fazer ali. */}
                            {isAereo && aprovacao.enviar?.pode && (
                                <button type="button" onClick={() => abrirConfirmacao('enviar')}
                                    disabled={decidindo || salvando}
                                    title={aprovacao.enviar.alterada
                                        ? 'Devolve a malha alterada ao RSVP.'
                                        : (aprovacao.precisaEventos ? 'Coloca a malha na fila de aprovação.' : 'Libera a malha para o RSVP importar.')}
                                    className="fin-pp__btn fin-pp__btn--tall fin-pp__btn--primary">
                                    {decidindo
                                        ? <span className="spinner-border spinner-border-sm" role="status" />
                                        : <FaPaperPlane size={14} />}
                                    {decidindo ? 'Enviando...' : (aprovacao.enviar.alterada ? 'Enviar alteração'
                                        : (aprovacao.precisaEventos ? 'Enviar para aprovação' : 'Enviar para o RSVP'))}
                                </button>
                            )}

                            {isAereo && outrasAcoes && <span style={{ width: 1, alignSelf: 'stretch', background: 'var(--fin-bd)' }} />}

                            {/* Eventos: aprova ou reprova a malha enviada — e a alterada, se o RSVP
                                a devolver. Reprovar leva observação, que vai no e-mail ao Aéreo. */}
                            {aprovacao.reprovar?.pode && (
                                <button type="button" onClick={() => abrirConfirmacao('reprovar')} disabled={decidindo}
                                    title="Reprova a malha e devolve ao Aéreo, com a observação."
                                    className="fin-pp__btn fin-pp__btn--tall fin-pp__btn--danger-ghost">
                                    <FaTimes size={14} />
                                    Reprovar
                                </button>
                            )}
                            {aprovacao.aprovar?.pode && (
                                <button type="button" onClick={() => abrirConfirmacao('aprovar')} disabled={decidindo}
                                    title={motivosTexto(aprovacao.motivos) || 'Aprova a malha.'}
                                    className="fin-pp__btn fin-pp__btn--tall fin-pp__btn--ok">
                                    {aprovacao.motivos?.length ? <FaExclamationTriangle size={14} /> : <FaCheck size={15} />}
                                    {admin ? 'Aprovar como Eventos' : 'Aprovar'}
                                </button>
                            )}

                            {/* RSVP: pede alteração da malha aprovada (a aprovação de Eventos fica) e,
                                quando o Aéreo reenvia, aprova a alterada ou a devolve para Eventos. */}
                            {acaoDoRsvp(aprovacao.pedirAlteracao?.pode) && (
                                <button type="button" onClick={() => abrirConfirmacao('alteracao')} disabled={decidindo || importando}
                                    title={aprovacao.situacao === 'alteracao'
                                        ? 'Já está em alteração — manda outra observação ao Aéreo.'
                                        : 'Devolve a malha ao Aéreo para mudança. A aprovação de Eventos continua gravada.'}
                                    className="fin-pp__btn fin-pp__btn--tall">
                                    <FaExchangeAlt size={14} />
                                    Pedir alteração
                                </button>
                            )}
                            {revisaAlteracao && (
                                <button type="button" onClick={() => abrirConfirmacao('revisarAlteracao')} disabled={decidindo}
                                    title="O Aéreo reenviou a malha com a alteração: aprove, ou peça a aprovação de Eventos."
                                    className="fin-pp__btn fin-pp__btn--tall fin-pp__btn--ok">
                                    <FaCheck size={15} />
                                    Aprovar alteração
                                </button>
                            )}

                            {/* Importação: marca-se na coluna "Importar" da planilha. */}
                            {selecaoImportacao && (
                                <>
                                    <span className="fin-pp__voo-info" style={{ fontSize: 12, color: 'var(--fin-mt)' }}>
                                        {ladosMarcados === 0
                                            ? 'Marque na planilha'
                                            : `${ladosMarcados} ${ladosMarcados !== 1 ? 'lados marcados' : 'lado marcado'}`}
                                    </span>
                                    {ladosMarcados > 0 && (
                                        <button type="button" disabled={importando}
                                            onClick={() => setMarcados({})}
                                            className="fin-pp__btn fin-pp__btn--tall">
                                            Limpar
                                        </button>
                                    )}
                                    <button type="button" onClick={() => abrirConfirmacao('importar')}
                                        disabled={importando || acoesImportar.length === 0}
                                        title="Leva os lados marcados para as opções do evento. Importar de novo atualiza sem duplicar."
                                        className="fin-pp__btn fin-pp__btn--tall fin-pp__btn--primary">
                                        {importando
                                            ? <span className="spinner-border spinner-border-sm" role="status" />
                                            : <FaPlaneDeparture size={14} />}
                                        {importando ? 'Importando...' : `Importar para o evento (${acoesImportar.length})`}
                                    </button>
                                </>
                            )}
                        </div>
                    </div>
                )}

                {/* O rascunho não tem aprovação: o rodapé é só o Salvar, que guarda a tela neste navegador. */}
                {rascunho && (
                    <div className="row fixoRodape">
                        <div className="col-12 d-flex align-items-center justify-content-end gap-3 flex-wrap">
                            <span style={{ fontSize: 12, color: 'var(--fin-mt)' }}>Fica só neste navegador: nada vai ao sistema.</span>
                            {botaoSalvar}
                        </div>
                    </div>
                )}

                {/* Modal de ajuste IDA & RETORNO */}
                <AjustarOpcoesModal
                    aberto={ajustarAberto}
                    opcoes={opcoes}
                    // A que está no evento anda como uma linha só, e não se agrupa nem se exclui lá.
                    importadas={portadasIds}
                    onFechar={() => setAjustarAberto(false)}
                    onAplicar={(novas) => {
                        // A volta que saiu da ida dela vem sem vínculo (`fundir`) e passa pelo vínculo automático, o
                        // mesmo do upload e do "Vincular órfãs": por rota na malha e, sem solicitação, pelo grupo da cidade
                        // e do destino, criado quando falta (todas numa chamada, para duas iguais dividirem o grupo
                        // novo). Sem casamento, fica órfã. Só ela: a órfã que já estava na tela continua como estava.
                        const soltas = novas.filter(o => o._voltaSolta).map(({ _voltaSolta, ...op }) => op);
                        let ligadas;
                        if (semPedido) {
                            const agrupadas = vincularPorCidade(soltas, trechos, idTemporario);
                            if (agrupadas.novos.length) setTrechos(prev => [...prev, ...agrupadas.novos]);
                            ligadas = agrupadas.opcoes;
                        } else {
                            ligadas = soltas.map(op => vincularOrfas([op], trechos).opcoes[0]);
                        }
                        let k = 0;
                        const relacionadas = novas.map(({ _voltaSolta, ...op }) => (_voltaSolta ? ligadas[k++] : op));
                        // prepara → reatribui `_id`; sem ele o board de agrupamento
                        // (reorganizar/removerOpcao casam por _id) para de funcionar.
                        setOpcoes(prepara(relacionadas));
                        setAjustarAberto(false);
                    }}
                />

                {/* Nova linha: pergunta o voo; nada entra na tabela antes do Adicionar, nem no banco antes do Salvar. */}
                <NovaOpcaoMalhaModal
                    aberto={novaLinhaAberta}
                    onFechar={() => setNovaLinhaAberta(false)}
                    onAdicionar={nova => adicionarLinhas([nova])} />

                {/* Modal de tipo: a dúvida da detecção e, na sugestão de voos, sempre. O detectado vem no botão primário. */}
                {precisaTipo && (
                    <div className="modal fade show d-block fin-pp fin-pp__modalbg"
                        onClick={(e) => { if (e.target === e.currentTarget) setPrecisaTipo(false); }}>
                        <div className="modal-dialog modal-dialog-centered">
                            <div className="modal-content fin-pp__modal">
                                <div className="fin-pp__modal-head">
                                    <span className="fin-pp__modal-head-ic"><FaFileExcel size={17} /></span>
                                    <h3 className="fin-pp__modal-title">Qual o tipo da planilha?
                                        <span className="fin-pp__modal-sub">
                                            {sugestaoTipo
                                                ? 'Identificamos o tipo automaticamente: confirme ou escolha outro.'
                                                : 'Não foi possível identificar automaticamente. Selecione o tipo do arquivo importado.'}
                                        </span>
                                    </h3>
                                    <button type="button" className="fin-pp__modal-close" aria-label="Fechar" onClick={() => setPrecisaTipo(false)}>
                                        <FaTimes size={16} />
                                    </button>
                                </div>
                                <div className="modal-body">
                                    {sugestaoMotivo && (
                                        <p className="fin-pp__cell-muted" style={{ fontSize: 12, margin: '0 0 12px' }}>{sugestaoMotivo}.</p>
                                    )}
                                    <div className="d-grid gap-2">
                                        {[
                                            { tipo: 'nacional_ida',     label: 'IDA Nacional' },
                                            { tipo: 'nacional_retorno', label: 'RETORNO Nacional' },
                                            { tipo: 'internacional',    label: 'Internacional (IDA + Retorno)' },
                                        ].map(({ tipo, label }) => {
                                            const sugerido = tipo === sugestaoTipo;
                                            return (
                                                <button key={tipo} type="button" onClick={() => escolherTipo(tipo)}
                                                    className={'fin-pp__btn fin-pp__btn--tall' + (sugerido ? ' fin-pp__btn--primary' : '')}
                                                    style={{ justifyContent: 'center', width: '100%' }}>
                                                    {label}
                                                    {sugerido && (
                                                        <span className="fin-pp__minitag"
                                                            style={{ marginLeft: 8, background: 'rgba(255,255,255,.22)', borderColor: 'transparent', color: '#fff' }}>
                                                            Sugerido
                                                        </span>
                                                    )}
                                                </button>
                                            );
                                        })}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                )}

                {/* Confirmação de toda ação que passa a vez para outro cargo, e da importação. */}
                {(() => {
                    if (!confirmacao) return null;
                    const emAlteracao = aprovacao?.situacao === 'alteracao';
                    const avisoMotivos = motivosTexto(aprovacao?.motivos);
                    const duplicatas = acoesImportar.filter(a => infoImportacao.get(a.malhaOpcao)?.estado === 'duplicata').length;
                    const atualizadas = acoesImportar.filter(a => infoImportacao.get(a.malhaOpcao)?.eventoAereo).length;
                    const cfg = {
                        // Nacional não passa por aprovação: o envio já libera a malha para o RSVP.
                        enviar: !aprovacao?.precisaEventos ? {
                            titulo: aprovacao?.enviar?.alterada ? 'Enviar a alteração ao RSVP?' : 'Enviar a malha para o RSVP?',
                            subtitulo: 'A malha é salva e fica liberada para o RSVP importar: evento nacional não passa por aprovação.',
                            notifica: 'Quem abriu a solicitação recebe o e-mail, com o coordenador de RSVP e a caixa do Aéreo em cópia.',
                            rotulo: aprovacao?.enviar?.alterada ? 'Enviar alteração' : 'Enviar', tom: 'primary', icone: 'enviar',
                        } : aprovacao?.enviar?.alterada ? {
                            titulo: 'Enviar a alteração ao RSVP?',
                            subtitulo: 'A malha é salva e volta para o RSVP, que aprova a alteração ou pede a aprovação de Eventos.',
                            notifica: 'Quem abriu a solicitação recebe o e-mail, com o coordenador de RSVP e a caixa do Aéreo em cópia.',
                            rotulo: 'Enviar alteração', tom: 'primary', icone: 'enviar',
                        } : {
                            titulo: 'Enviar a malha para aprovação?',
                            subtitulo: 'A malha é salva e vai para Eventos aprovar ou reprovar.',
                            notifica: 'Os responsáveis de Eventos do evento serão notificados por e-mail, com a caixa do Aéreo em cópia.',
                            rotulo: 'Enviar', tom: 'primary', icone: 'enviar',
                        },
                        notificar: {
                            titulo: 'Notificar a alteração da malha?',
                            // Nacional: ninguém de Eventos acompanha a malha; quem fica sabendo é o RSVP.
                            subtitulo: !aprovacao?.precisaEventos
                                ? 'A malha é salva, e o RSVP fica sabendo que ela mudou.'
                                : aprovacao?.situacao === 'aprovada'
                                    ? 'A malha é salva e continua aprovada — o que muda é que Eventos e o RSVP ficam sabendo que ela mudou depois da aprovação.'
                                    : 'A malha é salva e continua aguardando a aprovação de Eventos — o que muda é que eles ficam sabendo que ela mudou depois do envio.',
                            notifica: !aprovacao?.precisaEventos
                                ? 'Quem abriu a solicitação recebe o e-mail, com o coordenador de RSVP e a caixa do Aéreo em cópia.'
                                : aprovacao?.situacao === 'aprovada'
                                    ? 'Os responsáveis de Eventos e quem abriu a solicitação recebem o e-mail, com o coordenador de RSVP e a caixa do Aéreo em cópia.'
                                    : 'Os responsáveis de Eventos do evento serão notificados por e-mail, com a caixa do Aéreo em cópia.',
                            aviso: avisoOpcoesNoEvento(aprovacao?.portadas, 'desta malha', 'Notificar'),
                            corpo: <ObservacaoObrigatoria valor={observacao} onChange={setObservacao}
                                placeholder="O que mudou na malha" />,
                            preso: !observacao.trim(),
                            rotulo: 'Notificar', tom: 'primary', icone: 'enviar',
                        },
                        aprovar: {
                            titulo: aprovacao?.situacao === 'alterada' ? 'Aprovar a malha alterada?' : 'Aprovar a malha?',
                            subtitulo: 'A malha fica aprovada, e o RSVP pode importar as opções dela para o evento.',
                            notifica: 'O Aéreo e quem abriu a solicitação recebem o e-mail, com o coordenador de RSVP em cópia.',
                            // Não trava: diz o que conferir antes de aprovar.
                            aviso: avisoMotivos,
                            rotulo: 'Aprovar', tom: 'ok', icone: avisoMotivos ? 'aviso' : 'ok',
                        },
                        reprovar: {
                            titulo: 'Reprovar a malha?',
                            subtitulo: 'A malha volta para o Aéreo, que pode ajustar e reenviar esta malha ou mandar outra.',
                            notifica: 'O Aéreo será notificado por e-mail, com a observação.',
                            aviso: avisoOpcoesNoEvento(aprovacao?.portadas, 'desta malha', 'Reprovar'),
                            corpo: <ObservacaoObrigatoria valor={observacao} onChange={setObservacao} placeholder="Por que a malha foi reprovada" />,
                            preso: !observacao.trim(),
                            rotulo: 'Reprovar', tom: 'danger', icone: 'danger',
                        },
                        alteracao: {
                            titulo: emAlteracao ? 'Pedir alteração de novo?' : 'Pedir alteração da malha?',
                            subtitulo: emAlteracao
                                ? 'A malha continua em alteração — só a observação nova é enviada.'
                                : 'A malha volta para o Aéreo ajustar. A aprovação de Eventos continua gravada, e o reenvio volta para o RSVP.',
                            notifica: 'O Aéreo será notificado por e-mail, com a observação.',
                            aviso: avisoOpcoesNoEvento(aprovacao?.portadas, 'desta malha', 'Pedir alteração'),
                            corpo: <ObservacaoObrigatoria valor={observacao} onChange={setObservacao} />,
                            preso: !observacao.trim(),
                            rotulo: 'Pedir alteração', tom: 'primary', icone: 'alteracao',
                        },
                        // O RSVP decide se a alteração passa por Eventos de novo.
                        revisarAlteracao: {
                            titulo: 'Aprovar a alteração sem passar por Eventos?',
                            subtitulo: 'O Aéreo reenviou a malha com a alteração pedida.',
                            // Sem aprovação de Eventos gravada, só resta pedir a deles.
                            aviso: aprovacao?.confirmarAlteracao?.pode ? null : aprovacao?.confirmarAlteracao?.motivo,
                            corpo: (
                                <div style={{ lineHeight: 1.6 }}>
                                    <p style={{ margin: '0 0 8px' }}>
                                        <strong>Aprovar</strong>: a aprovação que Eventos já deu continua valendo, e a malha fica aprovada agora. O Aéreo e quem abriu a solicitação recebem o e-mail, com o coordenador de RSVP em cópia.
                                    </p>
                                    <p style={{ margin: 0 }}>
                                        <strong>Aprovar com Eventos</strong>: a aprovação anterior é apagada, e a malha vai para Eventos aprovar de novo. Os responsáveis de Eventos do evento são notificados por e-mail.
                                    </p>
                                </div>
                            ),
                            preso: !aprovacao?.confirmarAlteracao?.pode,
                            rotulo: 'Aprovar', rotuloFechar: 'Voltar', tom: 'ok', icone: 'ok',
                            alternativa: aprovacao?.solicitarEventos?.pode ? {
                                rotulo: 'Aprovar com Eventos',
                                tom: 'primary',
                                titulo: 'Apaga a aprovação anterior e manda a malha para Eventos aprovar de novo.',
                                onConfirmar: () => decidir('solicitarEventos'),
                            } : null,
                        },
                        // Alterada: o Importar é onde o RSVP aceita a alteração. A seleção fica
                        // guardada se ele mandar para Eventos.
                        importar: aprovacao?.importar?.alterada ? {
                            titulo: 'Aprovar a alteração e importar?',
                            subtitulo: `O Aéreo reenviou a malha com a alteração pedida. Importar ${acoesImportar.length} ${acoesImportar.length !== 1 ? 'opções' : 'opção'} aprova a alteração: a aprovação que Eventos já deu continua valendo.`,
                            aviso: aprovacao?.confirmarAlteracao?.pode ? null : aprovacao?.confirmarAlteracao?.motivo,
                            preso: !aprovacao?.confirmarAlteracao?.pode,
                            rotulo: 'Aprovar e importar', rotuloFechar: 'Voltar', tom: 'ok', icone: 'ok',
                            alternativa: aprovacao?.solicitarEventos?.pode ? {
                                rotulo: 'Aprovar com Eventos',
                                tom: 'primary',
                                titulo: 'Manda a malha para Eventos aprovar de novo. A seleção fica guardada para importar depois.',
                                onConfirmar: () => decidir('solicitarEventos'),
                            } : null,
                        } : {
                            titulo: `Importar ${acoesImportar.length} ${acoesImportar.length !== 1 ? 'opções' : 'opção'} para o evento?`,
                            subtitulo: 'Só os lados marcados entram. Os participantes da cidade de cada opção passam a vê-la. Importar de novo atualiza a mesma opção, sem duplicar.',
                            aviso: [
                                duplicatas > 0 && `${duplicatas} ${duplicatas !== 1 ? 'têm' : 'tem'} opção manual com o mesmo voo: importar cria outra ao lado.`,
                                atualizadas > 0 && `${atualizadas} já ${atualizadas !== 1 ? 'estão no evento e são atualizadas' : 'está no evento e é atualizada'} com a malha.`,
                            ].filter(Boolean).join(' ') || null,
                            rotulo: 'Importar', tom: 'primary', icone: 'enviar',
                        },
                    }[confirmacao];
                    if (!cfg) return null;

                    return (
                        <ConfirmarAcaoModal
                            aberto
                            titulo={cfg.titulo}
                            subtitulo={cfg.subtitulo}
                            notifica={cfg.notifica}
                            aviso={cfg.aviso}
                            rotulo={cfg.rotulo}
                            rotuloFechar={cfg.rotuloFechar}
                            tom={cfg.tom}
                            icone={cfg.icone}
                            processando={decidindo || importando}
                            confirmarPreso={!!cfg.preso}
                            alternativa={cfg.alternativa}
                            onConfirmar={confirmarAcao}
                            onFechar={() => setConfirmacao(null)}>
                            {cfg.corpo}
                        </ConfirmarAcaoModal>
                    );
                })()}

            </div>
            {atendimento.modal}
        </ErpTemplate>
    );
}
